import type { FileDocumentDto } from 'services/interface';

export interface BankDocSlotDefinition {
  document_type: string;
  label: string;
  required: boolean;
}

/** บุคคลธรรมดา — detect จาก description ที่มี "บุคคลธรรมดา" */
export const INDIVIDUAL_DOCUMENT_SLOTS: BankDocSlotDefinition[] = [
  {
    document_type: 'bank_book_copy',
    label:
      'สำเนาสมุดบัญชีเงินฝากธนาคาร (หน้าแรก) ที่ระบุชื่อบัญชีและเลขที่บัญชีชัดเจน',
    required: true,
  },
  {
    document_type: 'id_card_copy',
    label: 'สำเนาบัตรประจำตัวประชาชน (เซ็นรับรองสำเนาถูกต้อง)',
    required: true,
  },
];

/** นิติบุคคล — detect จาก "นิติบุคคล" หรือ "บริษัท" */
export const CORPORATE_DOCUMENT_SLOTS: BankDocSlotDefinition[] = [
  {
    document_type: 'bank_book_copy',
    label:
      'สำเนาสมุดบัญชีเงินฝากธนาคาร (หน้าแรก) (บัญชีในนามนิติบุคคล)',
    required: true,
  },
  {
    document_type: 'registration_copy',
    label: 'สำเนาหนังสือรับรองการจดทะเบียนนิติบุคคล',
    required: true,
  },
  /* ปิดชั่วคราว — ไม่แสดง ไม่บังคับอัพโหลด (เปิดกลับได้โดยคืนรายการด้านล่าง)
  {
    document_type: 'director_id_copy',
    label: 'สำเนาบัตรประจำตัวประชาชนของกรรมการ',
    required: true,
  },
  {
    document_type: 'power_of_attorney',
    label: 'หนังสือมอบอำนาจ (ถ้ามี)',
    required: false,
  },
  */
];

export function isIndividualBankType(description?: string | null): boolean {
  return (description ?? '').includes('บุคคลธรรมดา');
}

/** ประเภทบัญชีเป็นนิติบุคคลหรือไม่ — หน้าการเงินใช้ซ่อนแบบฟอร์มใบรับเงิน Non-VAT */
export function isCorporateBankType(description?: string | null): boolean {
  const d = description ?? '';
  return d.includes('นิติบุคคล') || d.includes('บริษัท');
}

export function getSlotsForBankType(description: string): BankDocSlotDefinition[] {
  const d = description ?? '';
  if (isIndividualBankType(d)) {
    return INDIVIDUAL_DOCUMENT_SLOTS;
  }
  if (isCorporateBankType(d)) {
    return CORPORATE_DOCUMENT_SLOTS;
  }
  return [];
}

/**
 * คีย์ slot ของเอกสารหนึ่งใบ
 * backend ฮาร์ดโค้ด document_type = "bank" ให้ทุกใบตอนสร้าง (createBankDocument)
 * คีย์จริงจึงถูกเก็บไว้ที่ field `name` — อ่าน name ก่อน แล้วค่อย fallback
 */
export function getDocumentSlotKey(
  doc: Pick<FileDocumentDto, "name" | "document_type">
): string | undefined {
  const key = doc.name || doc.document_type;
  return key && key !== "bank" ? key : undefined;
}

/** คืนเฉพาะรายการที่ document_type ยังมี slot ใน UI (กันเอกสารที่ปิดชั่วคราวถูกส่งไป API) */
export function filterDocumentsToActiveSlots(
  docs: FileDocumentDto[],
  bankTypeDescription: string
): FileDocumentDto[] {
  const allowed = new Set(
    getSlotsForBankType(bankTypeDescription).map((s) => s.document_type)
  );
  return docs.filter(
    (d) =>
      d.document_type != null &&
      d.document_type !== '' &&
      allowed.has(d.document_type)
  );
}
